import { FC, ReactNode } from 'react';
import { styled, Box, InputLabel, FormHelperText } from '@mui/material';
import RadioGroup, { RadioGroupProps } from '@mui/material/RadioGroup';
import { Controller, useFormContext } from 'react-hook-form';
import Radio from '@mui/material/Radio';
import './form-tri-state-switch-field.scss';
import { FormFieldContainer } from './form-field-container';
import { ConvergeInputLabel } from './converge-input-label';
import { useSetDirtyValue } from '../hooks';

interface IStyledRadioGroup extends RadioGroupProps {
  backgroundColor?: string;
}

const StyledRadioGroup = styled(RadioGroup, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<IStyledRadioGroup>(({ backgroundColor }) => ({
  backgroundColor,
  padding: '3px',
  borderRadius: '20px',
  transition: 'all 0.4s ease-in-out',
}));

const StyledRadioFelid = styled(Radio)(() => ({
  padding: 0,
}));

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  padding: '0 10px',
  columnGap: '8px',
}));

type FormTriStateSwitchFieldProps = {
  name: string;
  trueLabel?: string;
  falseLabel?: string;
  defaultValue?: boolean | null | undefined;
  disabled?: boolean;
  label: ReactNode;
  required?: boolean;
  containerWidth?: number;
  good?: boolean;
  onChangeValue?: (value: boolean | null) => void;
} & RadioGroupProps;

export const FormTriStateSwitchField: FC<FormTriStateSwitchFieldProps> = ({
  name,
  label,
  trueLabel = 'Yes',
  falseLabel = 'No',
  defaultValue,
  disabled = false,
  required = false,
  containerWidth = 12,
  good,
  onChangeValue,
  ...otherProps
}) => {
  const { control } = useFormContext();
  const { setDirtyValue } = useSetDirtyValue();

  const getColor = (
    value: boolean | null,
    disabled: boolean,
    good: boolean | undefined
  ) => {
    let color = '#c4c4c4';
    let green = '#20bb49';
    let red = '#DB524C';
    if (disabled) {
      green = '#b9dfba';
      red = '#dfb9b9';
    }

    if (value === true && good === true) {
      color = green;
    } else if (value === false && good === true) {
      color = red;
    } else if (value === true && good === false) {
      color = red;
    } else if (value === false && good === false) {
      color = green;
    }

    return color;
  };

  const convertValues = (value: boolean | null) => {
    if (value === true) {
      return 'true';
    }
    if (value === false) {
      return 'false';
    }
    return '';
  };
  const choosePos = (currentPos: boolean | null) => {
    const flags = [true, null, false];
    const i = flags.indexOf(currentPos) + 1;
    const n = flags.length;
    return flags[((i % n) + n) % n];
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ...otherField }, fieldState }) => (
        <FormFieldContainer
          md={containerWidth ?? 12}
          className="tri-state-switch-felid-container"
        >
          <ConvergeInputLabel required={required} label={label} />
          <StyledBox>
            <InputLabel
              onClick={() => {
                if (disabled) {
                  return;
                }

                setDirtyValue(name, value === true ? null : true);
              }}
              sx={{ cursor: disabled ? undefined : 'pointer' }}
            >
              {trueLabel}
            </InputLabel>
            <StyledRadioGroup
              className={`tri-state-switch-felid ${disabled ? 'disabled' : ''}`}
              row
              backgroundColor={getColor(value ?? defaultValue, disabled, good)}
              value={convertValues(value ?? defaultValue)}
              defaultValue={convertValues(defaultValue)}
              sx={{ minWidth: '44px' }}
              onChange={(e, value) => {
                if (disabled) {
                  return;
                }
                switch (value) {
                  case 'true':
                    if (onChangeValue) {
                      onChangeValue(true);
                    }
                    setDirtyValue(name, true);
                    break;
                  case 'false':
                    if (onChangeValue) {
                      onChangeValue(false);
                    }
                    setDirtyValue(name, false);
                    break;
                  default:
                    if (onChangeValue) {
                      onChangeValue(null);
                    }
                    setDirtyValue(name, null);
                    break;
                }
              }}
              {...otherField}
              {...otherProps}
            >
              <StyledRadioFelid
                value={true}
                disableRipple
                checkedIcon={<CheckedIcon />}
                icon={<UNCheckedIcon />}
                data-testid="tri-switch-true"
                disabled={disabled}
                onClick={() => {
                  if (disabled) {
                    return;
                  }
                  setDirtyValue(name, choosePos(true));
                }}
              />
              <StyledRadioFelid
                value=""
                disableRipple
                checkedIcon={<CheckedIcon />}
                icon={<UNCheckedIcon />}
                data-testid="tri-switch-null"
                disabled={disabled}
                onClick={() => {
                  setDirtyValue(name, choosePos(null));
                }}
              />
              <StyledRadioFelid
                value={false}
                disableRipple
                checkedIcon={<CheckedIcon />}
                icon={<UNCheckedIcon />}
                data-testid="tri-switch-false"
                disabled={disabled}
                onClick={() => {
                  if (disabled) {
                    return;
                  }
                  setDirtyValue(name, choosePos(false));
                }}
              />
            </StyledRadioGroup>
            <InputLabel
              sx={{ cursor: disabled ? undefined : 'pointer' }}
              onClick={() => {
                if (disabled) {
                  return;
                }
                setDirtyValue(name, value === false ? null : false);
              }}
            >
              {falseLabel}
            </InputLabel>
          </StyledBox>
          {fieldState.error && typeof fieldState.error === 'string' && (
            <FormHelperText
              error
              className="tri-state-switch-filed-form-helper-text"
            >
              {fieldState.error}
            </FormHelperText>
          )}
        </FormFieldContainer>
      )}
    />
  );
};

const CheckedIcon = () => <span className="checked-icon radio-icon"></span>;

const UNCheckedIcon = () => (
  <span className="un-checked-icon radio-icon"></span>
);
